import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import { BASE_URL } from '../config';
import { deleteFcmToken, requestFcmToken } from '../firebase/firebase-config';
import { useAxios } from '../hooks/useAxios';

interface UserInfo {
  firstName: string;
  lastName: string;
  mail: string;
  profilePhoto: string;
  photos: string[];
}

interface AuthContextType {
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  userInfo?: UserInfo;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  register: (firstName: string, lastName: string, email: string, password: string) => Promise<void>;
  refresh?: () => Promise<boolean>;
  refreshUserInfo?: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  token: null,
  isAuthenticated: false,
  isLoading: true,
  userInfo: undefined,
  login: async () => { },
  logout: async () => { },
  register: async () => { },
  refresh: async () => Promise.resolve(false),
  refreshUserInfo: async () => { },
});

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [token, setToken] = useState<string | null>(localStorage.getItem('accessToken'));
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [userInfo, setUserInfo] = useState<UserInfo | undefined>(() => {
    const stored = localStorage.getItem('userInfo');
    return stored ? JSON.parse(stored) : undefined;
  });

  const location = useLocation();
  const navigate = useNavigate();
  const axiosInstance = useAxios();

  const isAccessTokenValid = (accessToken: string | null): boolean => {
    if (!accessToken) return false;
    try {
      const payload = JSON.parse(atob(accessToken.split('.')[1]));
      const now = Math.floor(Date.now() / 1000);
      return payload.exp > now;
    } catch {
      return false;
    }
  };

  const fetchUserInfo = async (accessToken: string) => {
    try {
      const res = await axios.get(`${BASE_URL}/api/user/info`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      const data: UserInfo = res.data.data;
      localStorage.setItem('userInfo', JSON.stringify(data));
      setUserInfo(data);
    } catch (err) {
      console.error('Failed to fetch user info:', err);
    }
  };

  const refreshUserInfo = async () => {
    const accessToken = localStorage.getItem('accessToken');
    if (accessToken && isAccessTokenValid(accessToken)) {
      await fetchUserInfo(accessToken);
    }
  };

  const refreshPromiseRef = useRef<Promise<boolean> | null>(null);
  const isRefreshTokenValid = (refreshToken: string | null): boolean => {
    if (!refreshToken) return false;
    try {
      const payload = JSON.parse(atob(refreshToken.split('.')[1]));
      const now = Math.floor(Date.now() / 1000);
      return payload.exp > now;
    } catch {
      return false;
    }
  };

  const tryRefreshToken = async (): Promise<boolean> => {
    if (refreshPromiseRef.current) return refreshPromiseRef.current;

    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken || !isRefreshTokenValid(refreshToken)) {
      return false;
    }

    refreshPromiseRef.current = axios.post(`${BASE_URL}/auth/user/refresh-token`, { refreshToken })
      .then((res) => {
        const { accessToken: newToken, refreshToken: newRefresh } = res.data.data;
        localStorage.setItem('accessToken', newToken);
        localStorage.setItem('refreshToken', newRefresh);
        setToken(newToken);
        fetchUserInfo(newToken);
        return true;
      })
      .catch((error) => {
        console.error('Token refresh failed:', error);
        return false;
      })
      .finally(() => {
        refreshPromiseRef.current = null;
      });

    return refreshPromiseRef.current;
  };

  useEffect(() => {
    const checkAuth = async () => {
      const accessToken = localStorage.getItem('accessToken');

      if (isAccessTokenValid(accessToken)) {
        setIsAuthenticated(true);
        if (accessToken && !userInfo) {
          await fetchUserInfo(accessToken);
        }
      } else {
        const refreshed = await tryRefreshToken();
        if (!refreshed && !['/login', '/register', '/reset-password', '/'].includes(location.pathname)) {
          await logout();
          return;
        }
      }

      setIsLoading(false);
    };

    checkAuth();
  }, [location.pathname]);

  const login = async (username: string, password: string) => {
    const res = await axios.post(`${BASE_URL}/auth/user/login`, { username, password });
    const { accessToken: newToken, refreshToken: newRefresh } = res.data.data;

    localStorage.setItem('accessToken', newToken);
    localStorage.setItem('refreshToken', newRefresh);
    setToken(newToken);
    setIsAuthenticated(true);
    await fetchUserInfo(newToken);
    await registerFcmToken(newToken);
    navigate('/');
  };

  const registerFcmToken = async (accessToken: string) => {
    try {
      const swRegistration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
      const fcmToken = await requestFcmToken(swRegistration);

      if (!fcmToken) {
        console.warn('FCM token is null, registration skipped.');
        return;
      }

      const storedToken = localStorage.getItem('fcmToken');
      if (storedToken && storedToken === fcmToken) {
        console.log('FCM token already registered, skipping...');
        return;
      }

      await axios.post(
        `${BASE_URL}/auth/notify/device/register`,
        { fcmToken, platform: 'WEB' },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );

      localStorage.setItem('fcmToken', fcmToken);
    } catch (err) {
      console.error('Failed to register FCM token:', err);
    }
  };

  const register = async (firstName: string, lastName: string, email: string, password: string) => {
    const res = await axios.post(`${BASE_URL}/auth/user/register`, {
      firstName,
      lastName,
      email,
      password,
    });

    const { accessToken: newToken, refreshToken: newRefresh } = res.data.data;

    localStorage.setItem('accessToken', newToken);
    localStorage.setItem('refreshToken', newRefresh);
    setToken(newToken);
    setIsAuthenticated(true);
    await fetchUserInfo(newToken);
    await registerFcmToken(newToken);
  };

  const logout = useCallback(async () => {
    const refreshToken = localStorage.getItem('refreshToken');
    const fcmToken = localStorage.getItem('fcmToken');

    try {
      if (fcmToken) {
        await axiosInstance.post('/auth/notify/device/unregister', { fcmToken });
      }
      if (refreshToken) {
        await axios.post(`${BASE_URL}/auth/user/logout`, { refreshToken });
      }

    } catch (err) {
      console.warn('Logout failed:', err);
    }

    localStorage.clear();
    setToken(null);
    setIsAuthenticated(false);
    setUserInfo(undefined);
    await deleteFcmToken();
    localStorage.removeItem('fcmToken');

    navigate('/', { replace: true });
  }, []);

  const refresh = useCallback(async (): Promise<boolean> => {
    try {
      const success = await tryRefreshToken();
      setIsAuthenticated(success);

      if (success) {
        const token = localStorage.getItem('accessToken');
        if (token) {
          await fetchUserInfo(token);
          const storedFcm = localStorage.getItem('fcmToken');
          if (!storedFcm) {
            await registerFcmToken(token);
          }
        }
      }

      return success;
    } catch {
      return false;
    }
  }, []);
  return (
    <AuthContext.Provider
      value={{
        token,
        isAuthenticated,
        isLoading,
        userInfo,
        login,
        logout,
        register,
        refresh: tryRefreshToken,
        refreshUserInfo
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);